import { useEffect, useRef } from "react"
import { create } from "zustand"

import { cn } from "@productlane/lib/src/cn"
import { ProductlaneIcon } from "@productlane/ui/src/icons/productlane-icon"

import { SETUP_GUIDE_SEED } from "@/lib/agent-assistant-seeds"
import { useCopilotState } from "@/lib/states/copilot-state"
import { useSetupWorkspace } from "@/lib/states/setup-workspace-state"

export const FIRST_RUN_ATTRIBUTE = "data-first-run"
export const FIRST_RUN_DURATION_MS = 900
// Nothing signalled ready (hidden tab, unexpected route), so lift the veil anyway.
// Generous, because it now also covers the app's cold boot.
export const FIRST_RUN_ARM_TIMEOUT_MS = 4000
// Hydration keeps rendering for a while after mount; hold the veil until frames
// come in on time, then animate. Capped so a busy thread cannot stall the reveal.
const QUIET_FRAMES = 3
const QUIET_FRAME_BUDGET_MS = 20
const QUIET_WAIT_CAP_MS = 1200

type Phase = "idle" | "armed" | "playing"

interface FirstRunRevealState {
	phase: Phase
	arm: () => void
	play: () => void
	finish: () => void
}

export const useFirstRunReveal = create<FirstRunRevealState>((set) => ({
	phase: "idle",
	arm: () => set({ phase: "armed" }),
	play: () =>
		set((state) => (state.phase === "armed" ? { phase: "playing" } : state)),
	finish: () => set({ phase: "idle" }),
}))

function prefersReducedMotion() {
	return (
		typeof window !== "undefined" &&
		typeof window.matchMedia === "function" &&
		window.matchMedia("(prefers-reduced-motion: reduce)").matches
	)
}

/**
 * Starts the reveal once frames are landing on time, so the animation never
 * competes with hydration and stutters.
 */
export function useFirstRunRevealTrigger() {
	const phase = useFirstRunReveal((state) => state.phase)
	const play = useFirstRunReveal((state) => state.play)

	useEffect(() => {
		if (phase !== "armed") return

		let frame = 0
		let quietFrames = 0
		let last = performance.now()
		const startedAt = last

		const step = (now: number) => {
			const delta = now - last
			last = now
			quietFrames = delta < QUIET_FRAME_BUDGET_MS ? quietFrames + 1 : 0
			if (quietFrames >= QUIET_FRAMES || now - startedAt > QUIET_WAIT_CAP_MS) {
				play()
				return
			}
			frame = requestAnimationFrame(step)
		}
		frame = requestAnimationFrame(step)

		return () => cancelAnimationFrame(frame)
	}, [phase, play])
}

function openSetupGuide() {
	useCopilotState.getState().openWith({ kind: "workspace" }, SETUP_GUIDE_SEED)
}

export function FirstRunReveal() {
	const phase = useFirstRunReveal((state) => state.phase)
	const arm = useFirstRunReveal((state) => state.arm)
	const finish = useFirstRunReveal((state) => state.finish)
	const justCompletedSetup = useSetupWorkspace(
		(state) => state.justCompletedSetup,
	)
	const consumeSetupCompletion = useSetupWorkspace(
		(state) => state.consumeSetupCompletion,
	)
	const hasArmedRef = useRef(false)
	const guidePendingRef = useRef(false)

	useEffect(() => {
		if (hasArmedRef.current || !justCompletedSetup) return
		hasArmedRef.current = true
		// Consumed up front so a reload mid-reveal cannot replay it.
		consumeSetupCompletion()
		if (prefersReducedMotion()) {
			openSetupGuide()
			return
		}
		guidePendingRef.current = true
		arm()
	}, [justCompletedSetup, consumeSetupCompletion, arm])

	// The guide waits for the veil to lift, so the panel never slides in under it.
	useEffect(() => {
		if (phase !== "idle" || !guidePendingRef.current) return
		if (useFirstRunReveal.getState().phase !== "idle") return
		guidePendingRef.current = false
		openSetupGuide()
	}, [phase])

	useEffect(() => {
		if (phase !== "armed") return
		const timeout = setTimeout(finish, FIRST_RUN_ARM_TIMEOUT_MS)
		return () => clearTimeout(timeout)
	}, [phase, finish])

	useEffect(() => {
		if (phase !== "playing") return
		document.documentElement.setAttribute(FIRST_RUN_ATTRIBUTE, "true")
		const timeout = setTimeout(finish, FIRST_RUN_DURATION_MS)

		return () => {
			clearTimeout(timeout)
			document.documentElement.removeAttribute(FIRST_RUN_ATTRIBUTE)
		}
	}, [phase, finish])

	if (phase === "idle") return null

	return (
		<div
			aria-hidden
			className={cn(
				"pointer-events-none fixed inset-0 z-[60] flex items-center justify-center bg-gray-100 dark:bg-gray-950",
				phase === "playing" && "first-run-veil",
			)}
		>
			<div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#191A1C] ring-1 ring-white/10">
				<ProductlaneIcon fill="white" className="h-10" />
			</div>
		</div>
	)
}
